import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GridApi } from 'ag-grid-community';
import type { AxiosError } from 'axios';
import { atom, useAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { groupBy, isEmpty, sortBy, uniq } from 'lodash';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import {
  type ExtendedData,
  filterByDatetime,
  type TimePresetKeyWithField
} from '@/components/TimeFilter';
import {
  ASSETS_FILTERS_STORAGE_KEY,
  ASSETS_TIME_FILTER_STORAGE_KEY,
  DEFAULT_GRID_PAGE_SIZE
} from '@/constants/common';
import type { SearchFilters } from '@/features/SearchBar';
import useHandleError from '@/hooks/useHandleError';
import {
  type Asset,
  type AssetAlertsByNameDTO,
  type AssetAlertsDTO,
  type AssetConnectionMetricsDTO,
  type AssetDTO,
  type AssetListDTO,
  type AssetServiceMetricsDTO,
  type AssetTagCreateBaseDTO,
  renameShieldKeys
} from '@/pages/Assets';
import {
  organizationIdNameAtom,
  selectedOrganizationAtom
} from '@/pages/Organizations';
import { axiosClient } from '@/services/BaseApi.service';
import getPonduranceScoreSeverity from '@/utils/getPonduranceScoreSeverity';

import type { VmpVulnerabilityV2AssetListDTO } from '../VulnerabilityManagement/VulnerabilityManagement.types';

const createJotaiSessionStorage = <Value>() =>
  createJSONStorage<Value>(() => sessionStorage);

export const assetsAtom = atom<Asset[]>([]);
export const assetsFiltersAtom = atomWithStorage<SearchFilters>(
  ASSETS_FILTERS_STORAGE_KEY,
  {},
  createJotaiSessionStorage<SearchFilters>()
);
export const assetsGridApiAtom = atom<GridApi | null>(null);
export const assetsTableOrderAtom = atomWithStorage<'asc' | 'desc' | null>(
  'AssetsTableOrder',
  null
);
export const assetsTableOrderByAtom = atomWithStorage<string | null>(
  'AssetsTableOrderBy',
  null
);
export const assetsTableRowsAtom = atomWithStorage(
  'AssetsTableRows',
  DEFAULT_GRID_PAGE_SIZE
);
export const assetsTimeFilterAtom = atomWithStorage<TimePresetKeyWithField>(
  ASSETS_TIME_FILTER_STORAGE_KEY,
  'all|U',
  createJotaiSessionStorage<TimePresetKeyWithField>()
);
export const isAssetsPanelOpenAtom = atom(false);
export const isSelectedAssetLoadingAtom = atom(false);
export const selectedAssetAtom = atom<AssetDTO | null>(null);
export const selectedAssetExternalDetailsAtom = atom<AssetListDTO | null>(null);
export const selectedAssetIdAtom = atom<number | null>(null);

export const assetRiskScoreDialogAtom = atom<AssetDTO | null>(null);

const applyFilters = (
  items: Asset[],
  filters: SearchFilters,
  time: TimePresetKeyWithField,
  timeField?: string
) => {
  let filteredAssets = items;

  if (isEmpty(filters) && isEmpty(time)) return items;

  filteredAssets = time
    ? (filterByDatetime(
        items as ExtendedData[],
        time,
        timeField || undefined
      ) as Asset[])
    : filteredAssets;

  return filteredAssets.filter((item: Asset) =>
    Object.keys(filters).every((k) =>
      filters[k].some((f) => {
        if (k === 'compliance_tags' && item.compliance_tags) {
          return filters[k].some((g) =>
            item.compliance_tags?.some((tag) => tag.tag_name === g)
          );
        }

        if (k === 'criticality' && item.criticality) {
          return item.criticality.toLowerCase() === f.toLowerCase();
        }

        if (k === 'data_provider' && item.data_provider) {
          return item.data_provider.some(
            (provider: string) => provider.toLowerCase() === f.toLowerCase()
          );
        }

        if (k === 'operating_system' && item.operating_system) {
          return item.operating_system.toLowerCase() === f.toLowerCase();
        }

        if (k === 'score_severity' && item.score_severity) {
          return item.score_severity.toLowerCase() === f.toLowerCase();
        }

        if (k === 'any') {
          return JSON.stringify(Object.values(item))
            .toLocaleLowerCase()
            .includes(f.toLocaleLowerCase());
        }

        if (k in item) {
          const itemValue = item[k as keyof typeof item];
          if (typeof itemValue === 'string') {
            return itemValue.toLowerCase().includes(f.toLowerCase());
          }
          if (typeof itemValue === 'number') {
            return itemValue.toString() === f;
          }
        }

        return false;
      })
    )
  );
};

export const filteredAssetsAtom = atom((get) => {
  const assets = get(assetsAtom);
  const filters = get(assetsFiltersAtom);
  const time = get(assetsTimeFilterAtom);
  const timeField = time.includes('|C') ? 'created_at' : 'last_seen_at';

  if (!assets.length) {
    return [];
  }
  return applyFilters(assets, filters, time, timeField);
});

export const assetQueryKeys = {
  all: ['assets'] as const,
  list: (organizationLabel: string) =>
    ['assets', 'list', organizationLabel] as const,
  detail: (assetId: number) => ['assets', 'detail', assetId] as const
};

export const useFetchAssets = () => {
  const [, setAssets] = useAtom(assetsAtom);
  const [organizationIdNames] = useAtom(organizationIdNameAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const handleError = useHandleError();

  const { data, isError, error, refetch, isSuccess, isLoading, isFetching } =
    useQuery({
      queryKey: assetQueryKeys.list(selectedOrganization.label),
      queryFn: () =>
        selectedOrganization.label === 'all'
          ? axiosClient.get('/assets/').then((resp) => resp.data)
          : axiosClient
              .get(`organizations/${selectedOrganization.label}/assets`)
              .then((resp) => resp.data),
      select: (assets: Asset[]) =>
        assets.map((asset) => ({
          ...asset,
          data_provider:
            sortBy(
              uniq(
                asset.external_details?.map((external_detail) =>
                  external_detail.provider.replace('shield', 'vmpe')
                )
              )
            ) || [],
          organization_name:
            organizationIdNames[asset.organization_id] || undefined,
          score_severity: getPonduranceScoreSeverity(asset.risk_score || 0)
        })),
      staleTime: 60 * 1000
    });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch assets.');
    }
    if (isSuccess && data) {
      setAssets(data);
    }
  }, [isError, isSuccess, data]);

  return { isFetching, isLoading, refetch };
};

export const useAssetSelection = () => {
  const [, setSelectedAsset] = useAtom(selectedAssetAtom);
  const [, setSelectedAssetId] = useAtom(selectedAssetIdAtom);
  const [searchParams, setSearchParams] = useSearchParams();
  const [gridApi] = useAtom(assetsGridApiAtom);

  const deselectAsset = useCallback(() => {
    const updatedParams = new URLSearchParams(searchParams.toString());
    updatedParams.delete('asset');
    setSearchParams(updatedParams);
    setSelectedAsset(null);
    setSelectedAssetId(null);
    gridApi?.deselectAll();
  }, [
    gridApi,
    searchParams,
    setSearchParams,
    setSelectedAsset,
    setSelectedAssetId
  ]);

  const selectAsset = useCallback(
    (id: string) => {
      const updatedParams = new URLSearchParams(searchParams.toString());
      updatedParams.set('asset', id);
      setSearchParams(updatedParams);
      setSelectedAssetId(Number(id));
    },
    [searchParams]
  );

  return {
    deselectAsset,
    selectAsset
  };
};

export const useFetchAssetDetails = () => {
  const { deselectAsset } = useAssetSelection();
  const [, setSelectedAsset] = useAtom(selectedAssetAtom);
  const [selectedAssetId] = useAtom(selectedAssetIdAtom);
  const handleError = useHandleError();
  const navigate = useNavigate();

  const queryFn = () => {
    return axiosClient
      .get(`/assets/${selectedAssetId}`)
      .then((resp) => resp.data);
  };

  const { data, isError, error, refetch, isSuccess, isLoading, isFetching } =
    useQuery({
      queryKey: selectedAssetId
        ? assetQueryKeys.detail(selectedAssetId)
        : assetQueryKeys.all,
      queryFn,
      enabled: !!selectedAssetId,
      staleTime: 60 * 1000
    });

  useEffect(() => {
    if (isError) {
      deselectAsset();
      handleError(error as AxiosError, 'Failure to fetch asset details.');
    }
    if (isSuccess) {
      const asset =
        data.external_details && Array.isArray(data.external_details)
          ? {
              ...data,
              external_details: data.external_details.map(
                (detail: { provider: string }) => {
                  const updatedDetail =
                    detail.provider === 'shield'
                      ? { ...detail, provider: 'vmpe' }
                      : detail;
                  return renameShieldKeys(updatedDetail);
                }
              )
            }
          : data;
      setSelectedAsset(asset);
    }
    // Navigate to Asset Not Found page if 404 error
    if (isError && (error as AxiosError).status === 404) {
      navigate('/asset-not-found');
    }
  }, [data, isError, isSuccess]);

  return {
    isError,
    isSuccess,
    isFetching,
    isLoading,
    refetch,
    data
  };
};

export const useFetchAssetVulnerabilities = (assetId: number) => {
  const handleError = useHandleError();

  const { data, isError, error, isSuccess, isLoading } = useQuery<
    VmpVulnerabilityV2AssetListDTO[]
  >({
    queryKey: [`v2/vulnerabilities/by_asset/${assetId}`],
    staleTime: 60 * 1000,
    enabled: !!assetId
  });

  useEffect(() => {
    if (isError) {
      handleError(
        error as AxiosError,
        'Failure to fetch asset vulnerabilities.'
      );
    }
  }, [isError, isSuccess, data]);

  return { data, isLoading };
};

export const useFetchAssetRemediations = (assetId: number) => {
  const handleError = useHandleError();

  const { data, isError, error, isSuccess, isLoading } = useQuery<
    VmpVulnerabilityV2AssetListDTO[]
  >({
    queryKey: [`v2/vulnerabilities/by_asset/${assetId}?type=remediation`],
    staleTime: 60 * 1000,
    enabled: !!assetId
  });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch asset remediations.');
    }
  }, [isError, isSuccess, data]);

  return { data, isLoading };
};

export const useUpdateAssetTags = () => {
  const [selectedAsset] = useAtom(selectedAssetAtom);
  const handleError = useHandleError();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (tag: AssetTagCreateBaseDTO) => {
      if (!selectedAsset) {
        throw new Error('No asset selected');
      }
      return axiosClient.post('/assets_tags/', tag);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failed to add asset tag');
      enqueueSnackbar('Failed to add asset tag', { variant: 'error' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: assetQueryKeys.all
      });
    }
  });
};

export const useFetchAssetServices = () => {
  const [selectedAsset] = useAtom(selectedAssetAtom);
  const handleError = useHandleError();

  const { data, isError, error, isSuccess, isLoading } = useQuery<
    AssetServiceMetricsDTO[],
    AxiosError
  >({
    queryKey: [`asset_metrics/${selectedAsset?.id}/services`],
    staleTime: 60 * 1000,
    enabled: !!selectedAsset,
    select: (services) => {
      // Group services by port and service_name combination
      const groupedServices = groupBy(
        services,
        (service) => `${service.port}|${service.service_name}`
      );

      return Object.entries(groupedServices).map(([key, serviceGroup]) => {
        const [port, serviceName] = key.split('|');

        return {
          ...serviceGroup[0],
          service_name: serviceName,
          port: Number(port),
          direction: uniq(serviceGroup.map((s) => s.direction)).join(', ')
        };
      });
    }
  });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch asset services.');
    }
  }, [isError, isSuccess, data]);

  const sortedData = sortBy(data, 'port');

  return { data: sortedData, isLoading };
};

export const useFetchAssetAlerts = () => {
  const [selectedAsset] = useAtom(selectedAssetAtom);
  const handleError = useHandleError();

  const { data, isError, error, isSuccess, isLoading } = useQuery<
    AssetAlertsByNameDTO[],
    AxiosError
  >({
    queryKey: [`asset_metrics/${selectedAsset?.id}/alerts/by_name`],
    staleTime: 60 * 1000,
    enabled: !!selectedAsset
  });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch asset alerts.');
    }
  }, [isError, isSuccess, data]);

  return { data, isLoading, hasData: !!data && data.length > 0 };
};

export const useFetchAssetAlertsPerDay = () => {
  const [selectedAsset] = useAtom(selectedAssetAtom);
  const handleError = useHandleError();

  const { data, isError, error, isSuccess, isLoading } = useQuery<
    AssetAlertsDTO[],
    AxiosError
  >({
    queryKey: [`asset_metrics/${selectedAsset?.id}/alerts`],
    staleTime: 60 * 1000,
    enabled: !!selectedAsset
  });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch asset alerts.');
    }
  }, [isError, isSuccess, data]);

  return { data, isLoading, hasData: !!data && data.length > 0 };
};

export const useFetchAssetConnections = () => {
  const [selectedAsset] = useAtom(selectedAssetAtom);
  const handleError = useHandleError();

  const { data, isError, error, isSuccess, isLoading } = useQuery<
    AssetConnectionMetricsDTO[],
    AxiosError
  >({
    queryKey: [`asset_metrics/${selectedAsset?.id}/connections`],
    staleTime: 60 * 1000,
    enabled: !!selectedAsset,
    select: (connections) => connections.filter((item) => item.country_code) // Remove items without a usable country_code
  });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch asset connections.');
    }
  }, [isError, isSuccess, data]);

  return { data, isLoading };
};
